import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { PlusIcon, TrashIcon, PencilSimpleIcon, UsersIcon } from '@phosphor-icons/react';

import { PageHeader } from '../../../components/PageHeader';
import { PageHeading } from '../../../components/PageHeading';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { ITableHeader, Table } from '../../../components/table/Table';
import { Card } from '../../../components/Card';
import { EmptyState } from '../../../components/empty-state/EmptyState';
import { Pill, type PillColor } from '../../../components/pill/Pill';
import { UserIdentityCell } from '../../../components/user/UserIdentityCell';
import { Button } from '../../../components/button/Button';
import { ConfirmDialog } from '../../../components/dialog/ConfirmDialog';
import { Dialog } from '../../../components/dialog/Dialog';
import { Input } from '../../../components/input/Input';
import { SimpleSelect } from '../../../components/select/SimpleSelect';
import { useTranslation } from '../../../contexts/translation-context';
import { getDisplayError } from '../../../utils/get-display-error';
import { formatDateTime } from '../../../utils/date';
import {
  getCarrierPortalUsers,
  inviteCarrierPortalUser,
  deleteCarrierPortalInvite,
  updateCarrierPortalUserRole,
  renameCarrierPortalUser,
  CarrierPortalUser,
  CarrierPortalInvitedUser,
  CarrierUserRole,
} from '../models';
import { useAuth } from '../../../contexts/auth-context';
import { useCarrierPortalSupplier } from '../CarrierPortalCarrierContext';

interface IAccessRow {
  id: string;
  user: CarrierPortalUser | null;
  invite: CarrierPortalInvitedUser | null;
}

export const CarrierPortalUsersPage = () => {
  const { i18n } = useTranslation();
  const { me } = useAuth();
  const { carrierId } = useCarrierPortalSupplier();

  const ROLE_OPTIONS: { key: CarrierUserRole; name: string }[] = [
    { key: 'CarrierDispatcher', name: i18n('carrierPortal.users.roleDispatcher') },
    { key: 'CarrierDriver', name: i18n('carrierPortal.users.roleDriver') },
  ];

  const getRoleDisplayName = (role: string): string => {
    switch (role) {
      case 'CarrierDispatcher':
        return i18n('carrierPortal.users.roleDispatcher');
      case 'CarrierDriver':
        return i18n('carrierPortal.users.roleDriver');
      default:
        return role;
    }
  };
  const [users, setUsers] = useState<CarrierPortalUser[]>([]);
  const [invitedUsers, setInvitedUsers] = useState<CarrierPortalInvitedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<CarrierUserRole>('CarrierDispatcher');
  const [isInviting, setIsInviting] = useState(false);
  const [renameDialogUser, setRenameDialogUser] = useState<CarrierPortalUser | null>(null);
  const [renameName, setRenameName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  const loadUsers = async () => {
    try {
      const data = await getCarrierPortalUsers(carrierId);
      setUsers(data.users);
      setInvitedUsers(data.invitedUsers);
    } catch (err) {
      toast.error(getDisplayError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      toast.error(i18n('carrierPortal.users.emailRequired'));
      return;
    }

    setIsInviting(true);
    try {
      await inviteCarrierPortalUser(
        carrierId,
        inviteEmail.trim(),
        inviteRole,
        inviteName.trim() ? inviteName.trim() : undefined,
      );
      toast.success(i18n('carrierPortal.users.userInvited'));
      setInviteEmail('');
      setInviteName('');
      setInviteRole('CarrierDispatcher');
      setIsInviteDialogOpen(false);
      await loadUsers();
    } catch (err) {
      toast.error(getDisplayError(err));
    } finally {
      setIsInviting(false);
    }
  };

  const handleDeleteInvite = async (inviteId: string) => {
    try {
      await deleteCarrierPortalInvite(carrierId, inviteId);
      toast.success(i18n('carrierPortal.users.inviteRemoved'));
      await loadUsers();
    } catch (err) {
      toast.error(getDisplayError(err));
    }
  };

  const handleUpdateUserRole = async (userId: string, newRole: CarrierUserRole) => {
    try {
      await updateCarrierPortalUserRole(carrierId, userId, newRole);
      toast.success(i18n('carrierPortal.users.roleUpdated'));
      await loadUsers();
    } catch (err) {
      toast.error(getDisplayError(err));
    }
  };

  const handleRenameUser = async () => {
    if (!renameDialogUser || !renameName.trim()) {
      return;
    }

    setIsRenaming(true);
    try {
      await renameCarrierPortalUser(carrierId, renameDialogUser.id, renameName.trim());
      toast.success(i18n('carrierPortal.users.userRenamed'));
      setRenameDialogUser(null);
      setRenameName('');
      await loadUsers();
    } catch (err) {
      toast.error(getDisplayError(err));
    } finally {
      setIsRenaming(false);
    }
  };

  const accessHeaders: ITableHeader[] = [
    { id: 'name', name: i18n('carrierPortal.users.name'), width: '260px' },
    { id: 'email', name: i18n('carrierPortal.users.email') },
    { id: 'role', name: i18n('carrierPortal.users.role'), width: '170px' },
    { id: 'status', name: i18n('carrierPortal.users.isActive'), width: '140px' },
    { id: 'date', name: i18n('carrierPortal.users.joined'), width: '170px' },
    { id: 'actions', name: '', width: '60px', align: 'right' },
  ];

  /**
   * An invitation is the same row as an account with a different status, so both
   * live in one table rather than in two separate ones.
   */
  const accessRows: IAccessRow[] = [
    ...users.map((user) => ({ id: `user-${user.id}`, user, invite: null })),
    ...invitedUsers.map((invite) => ({ id: `invite-${invite.id}`, user: null, invite })),
  ];

  const meta = [
    `${i18n('carrierPortal.users.activeUsers')}: ${users.length}`,
    invitedUsers.length ? `${i18n('carrierPortal.users.pendingInvitations')}: ${invitedUsers.length}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  if (loading) {
    return (
      <div>
        <PageHeader title={i18n('carrierPortal.users.users')} />
        <div className="p-7 text-sm text-fg-muted">{i18n('carrierPortal.loading')}</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={i18n('carrierPortal.users.users')} />

      <PageHeading
        noMarginBottom
        leftSide={<Breadcrumb currentItem={i18n('carrierPortal.users.users')} />}
        rightSide={
          <Button
            color="primary"
            iconLeft={<PlusIcon className="button-icon" />}
            onTrigger={() => setIsInviteDialogOpen(true)}
          >
            {i18n('carrierPortal.users.inviteUser')}
          </Button>
        }
      />

      <div className="grid gap-4 p-7">
        <Card
          title={
            <span className="flex items-baseline gap-2.5">
              {i18n('carrierPortal.users.users')}
              <span className="card-subtle-text font-normal">{meta}</span>
            </span>
          }
          flush
        >
          <Table
            idKey="id"
            headers={accessHeaders}
            data={accessRows}
            emptyState={<EmptyState icon={<UsersIcon />} title={i18n('carrierPortal.users.noUsersFound')} />}
            mapData={(row) => {
              const { user, invite } = row;

              if (invite) {
                return [
                  <UserIdentityCell name={invite.name ?? <span className="font-normal text-fg-muted">—</span>} />,
                  invite.email,
                  getRoleDisplayName(invite.role),
                  <Pill color={'warning' as PillColor}>{i18n('carrierPortal.users.invited')}</Pill>,
                  formatDateTime(invite.createdAt),
                  <ConfirmDialog
                    triggerColor="ghost"
                    triggerText={<TrashIcon className="button-icon" />}
                    title={i18n('carrierPortal.users.deleteInvitation')}
                    submitText={i18n('carrierPortal.users.removeInvite')}
                    description={i18n('carrierPortal.users.confirmRemoveInvite', {
                      email: invite.email,
                    })}
                    onSubmit={async () => {
                      await handleDeleteInvite(invite.id);
                    }}
                  />,
                ];
              }

              const isCurrentUser = user!.id === me.id;
              const selectedRole = ROLE_OPTIONS.find((r) => r.key === user!.role) ?? ROLE_OPTIONS[0];
              const isDispatcher = user!.role === 'CarrierDispatcher';

              return [
                <UserIdentityCell name={user!.name} />,
                user!.email,
                isCurrentUser ? (
                  getRoleDisplayName(user!.role)
                ) : (
                  <div className="min-w-[150px]" onClick={(e) => e.stopPropagation()}>
                    <SimpleSelect
                      items={ROLE_OPTIONS}
                      selectedItem={selectedRole}
                      onSelect={(item) => {
                        if (item && item.key !== user!.role) {
                          handleUpdateUserRole(user!.id, item.key as CarrierUserRole);
                        }
                      }}
                    />
                  </div>
                ),
                <Pill color={'positive' as PillColor}>{i18n('carrierPortal.users.active')}</Pill>,
                formatDateTime(user!.createdAt),
                isDispatcher && !isCurrentUser ? (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Button
                      color="ghost"
                      iconLeft={<PencilSimpleIcon className="button-icon" />}
                      onTrigger={() => {
                        setRenameDialogUser(user!);
                        setRenameName(user!.name);
                      }}
                    />
                  </div>
                ) : null,
              ];
            }}
          />
        </Card>
      </div>

      {/* Invite User Dialog */}
      <Dialog
        isOpen={isInviteDialogOpen}
        onClose={() => {
          setIsInviteDialogOpen(false);
          setInviteEmail('');
          setInviteName('');
          setInviteRole('CarrierDispatcher');
        }}
        title={i18n('carrierPortal.users.inviteUser')}
      >
        <div className="flex flex-col gap-4">
          <div>
            <label className="label-text mb-1 block">{i18n('carrierPortal.users.nameOptional')}</label>
            <Input
              type="text"
              value={inviteName}
              onChange={setInviteName}
              placeholder={i18n('carrierPortal.users.namePlaceholder')}
            />
          </div>
          <div>
            <label className="label-text mb-1 block">{i18n('carrierPortal.users.emailAddress')}</label>
            <Input
              type="email"
              value={inviteEmail}
              onChange={setInviteEmail}
              placeholder={i18n('carrierPortal.users.emailPlaceholder')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleInviteUser();
                }
              }}
            />
          </div>
          <div>
            <label className="label-text mb-1 block">{i18n('carrierPortal.users.role')}</label>
            <SimpleSelect
              items={ROLE_OPTIONS}
              selectedItem={ROLE_OPTIONS.find((r) => r.key === inviteRole) ?? ROLE_OPTIONS[0]}
              onSelect={(item) => {
                if (item) {
                  setInviteRole(item.key as CarrierUserRole);
                }
              }}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              color="secondary"
              onTrigger={() => {
                setIsInviteDialogOpen(false);
                setInviteEmail('');
                setInviteName('');
                setInviteRole('CarrierDispatcher');
              }}
              isDisabled={isInviting}
            >
              {i18n('carrierPortal.users.cancel')}
            </Button>
            <Button color="primary" onTrigger={handleInviteUser} isDisabled={isInviting}>
              {isInviting ? i18n('carrierPortal.users.sending') : i18n('carrierPortal.users.sendInvitation')}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Rename User Dialog */}
      <Dialog
        isOpen={renameDialogUser !== null}
        onClose={() => {
          setRenameDialogUser(null);
          setRenameName('');
        }}
        title={i18n('carrierPortal.users.renameUser')}
      >
        <div className="flex flex-col gap-4">
          <div>
            <label className="label-text mb-1 block">{i18n('carrierPortal.users.newName')}</label>
            <Input
              type="text"
              value={renameName}
              onChange={setRenameName}
              placeholder={i18n('carrierPortal.users.newName')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRenameUser();
                }
              }}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              color="secondary"
              onTrigger={() => {
                setRenameDialogUser(null);
                setRenameName('');
              }}
              isDisabled={isRenaming}
            >
              {i18n('carrierPortal.users.cancel')}
            </Button>
            <Button color="primary" onTrigger={handleRenameUser} isDisabled={isRenaming || !renameName.trim()}>
              {isRenaming ? i18n('carrierPortal.users.sending') : i18n('carrierPortal.users.save')}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
